import React, { useState } from 'react';
import { format } from 'date-fns';
import { useField } from 'informed';

import { DATE_PICKER_DATE_FORMAT } from '@nw/shared/config';

import { STRING_DELIMITER } from '../../constants/MiscellaneousConstants';
import { radioGroupTypes } from '../../constants/RadioGroupConstants';
import { RadioGroupVariant } from '../../types/RadioGroupTypes';

import { RadioGroup } from '../RadioGroup';

import {
  DayLabel,
  Divider,
  ErrorText,
  FieldContainer,
  Label,
  LabelAndCloseIconWrapper,
  Option,
  OptionsContainer,
  radioWrapperStyles,
  SelectWebinarSlotModalViewContainer,
  SlotCard,
  SlotCardsContainer,
  SlotOptionText,
  SlotTime,
  StyledTickIcon,
  TickIconContainer,
  TimeSlotsContainer
} from './StyledComponents';

interface SlotSelectionFieldFormattedOptionProps {
  field: string;
  supportingField: string;
  label?: string;
  options: any;
  validationSchemaObject: any;
  error: string;
  touched: boolean;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  slotUIType?: string;
  containerClassName?: string;
  labelClassName?: string;
  executeChildFunction: React.MutableRefObject<(value: string) => void | null>;
}

interface OptionType {
  value: string;
  label: string;
}

interface LabelAndOptionsType {
  date: Date;
  dayCode: number;
  label: string;
  options: OptionType[];
}

interface TimeSlotOption {
  label: string;
  formattedOptions: OptionType[];
  dayCode: number;
  date: Date;
  options: any[];
}

export const SlotSelectionFieldFormattedOption = (
  props: SlotSelectionFieldFormattedOptionProps
) => {
  const {
    field,
    label,
    options,
    validationSchemaObject,
    error,
    touched,
    validateOnBlur = true,
    validateOnChange = true,
    supportingField = '',
    slotUIType,
    containerClassName,
    labelClassName,
    executeChildFunction
  } = props;

  const { fieldState, fieldApi } = useField({
    field,
    validateOnBlur,
    validateOnChange,
    validationSchema: validationSchemaObject.fields[field],
    error: error,
    touched: touched,
    options
  });

  const { fieldApi: supportingFieldApi } = useField({
    field: supportingField,
    validateOnBlur,
    validateOnChange,
    error: '',
    touched: false
  });
  //NOTE:Have to fix field name for date till then using supportingField
  // const { fieldApi: webinar1DateTimeApi } = useField({
  //   field: WEBINAR1DATE_AND_TIME,
  //   validateOnBlur,
  //   validateOnChange,
  //   error: '',
  //   touched: false
  // })

  const { value } = fieldState;
  const { setValue } = fieldApi;

  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedValue, setSelectedValue] = useState<string>();

  const updateSelectedValue = (value: string): void => {
    setSelectedValue(value);
  };
  executeChildFunction.current = updateSelectedValue;

  const onClickOption = (option: OptionType | string, date: Date): void => {
    let value: string;
    setSelectedDate(date);
    if (typeof option !== 'string') {
      value = option.value;
    } else {
      value = option;
    }
    setSelectedValue(value);
    value = value.split(STRING_DELIMITER)[1];
    setValue(value);

    const webinar1DateTimeValue = format(date, DATE_PICKER_DATE_FORMAT);

    supportingFieldApi.setValue(webinar1DateTimeValue);
    //NOTE:Have to fix field name for date till then using supportingField
    // webinar1DateTimeApi.setValue(webinar1DateTimeValue)
  };

  const isOptionSelected = (option: OptionType, date: Date): boolean => {
    return (
      option.value.split(STRING_DELIMITER)[1] === value &&
      String(date) === String(selectedDate)
    );
  };

  const shouldRenderDividerLine = (index: number): boolean => {
    return index < options.length - 1 && options.length > 1;
  };

  const renderRadioGroupSlotUIType = (
    values: LabelAndOptionsType
  ): React.ReactElement => {
    const { label, options, date } = values;
    return (
      <FieldContainer key={date}>
        <RadioGroup
          field={field}
          label={label}
          touched={touched}
          error={''}
          radioOptions={options}
          validationSchemaObject={validationSchemaObject}
          onChange={(option) => onClickOption(option, date)}
          type={radioGroupTypes.simple as RadioGroupVariant}
          labelClassName={'radioGroupLabelClassName'}
          radioWrapperStyles={radioWrapperStyles}
          radioClassName={'radioButtonClassName'}
          isMarginBottomZero={true}
          value={selectedValue}
        />
      </FieldContainer>
    );
  };

  const renderCardSlotUIType = (
    values: LabelAndOptionsType
  ): React.ReactElement => {
    const { options, date } = values;
    return (
      <SlotCardsContainer>
        {options.map((option) => {
          const { label, value } = option;
          const isSelected = isOptionSelected(option, date);
          return (
            <SlotCard
              key={value}
              onClick={() => onClickOption(option, date)}
              isSelected={isSelected}
              type="button"
              className={
                isSelected
                  ? 'slotCardClassName slotCardSelectedClassName'
                  : 'slotCardClassName'
              }
            >
              <SlotTime>{label}</SlotTime>
            </SlotCard>
          );
        })}
      </SlotCardsContainer>
    );
  };

  const renderLabelAndOptions = (
    values: LabelAndOptionsType
  ): React.ReactElement => {
    const { label, options, date } = values;

    if (slotUIType === 'CARD') {
      return renderCardSlotUIType(values);
    } else if (slotUIType) {
      return renderRadioGroupSlotUIType(values);
    }

    return (
      <TimeSlotsContainer>
        <LabelAndCloseIconWrapper>
          <DayLabel className={'dayLabelClassName'}>{label}</DayLabel>
        </LabelAndCloseIconWrapper>
        <OptionsContainer>
          {options.map((option) => {
            const { label } = option;
            const isSelected = isOptionSelected(option, date);
            return (
              <Option
                key={option.value}
                onClick={() => onClickOption(option, date)}
                isSelected={isSelected}
                type="button"
                className={
                  isSelected
                    ? 'slotOptionsContainerClassName slotSelectedOptionsContainerClassName'
                    : 'slotOptionsContainerClassName'
                }
              >
                <TickIconContainer
                  width={16}
                  className={'tickIconContainerClassName'}
                >
                  {isSelected ? <StyledTickIcon width={16} /> : null}
                </TickIconContainer>
                <SlotOptionText
                  isSelected={isSelected}
                  className={
                    isSelected ? 'slotOptionSelectedTextClassName' : ''
                  }
                >
                  {label}
                </SlotOptionText>
              </Option>
            );
          })}
        </OptionsContainer>
      </TimeSlotsContainer>
    );
  };

  return (
    <>
      <Label className={`${labelClassName} slotSelectionLabelClassName`}>
        {label}
      </Label>
      <SelectWebinarSlotModalViewContainer className={containerClassName}>
        {options.map((timeSlotOption: TimeSlotOption, index: number) => {
          return (
            timeSlotOption.options.length > 0 && (
              <>
                {renderLabelAndOptions({
                  label: timeSlotOption.label,
                  options: timeSlotOption.formattedOptions,
                  dayCode: timeSlotOption.dayCode,
                  date: timeSlotOption.date
                })}

                {shouldRenderDividerLine(index) && !slotUIType && <Divider />}
              </>
            )
          );
        })}
        <ErrorText>{error}</ErrorText>
      </SelectWebinarSlotModalViewContainer>
    </>
  );
};
